import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type {
  AgencyProviderPackageModel, AgencyProviderPackageRequestOptions,
} from '@models/types'

import type { ModuleState } from '@redux/modules/types'
import { EntitiesState } from '@redux/entities'

const watchEntityKeys = ['agencies', 'agencyProviderPackages', 'providerPackages']

type HandleSortParams = {
  agencies: EntitiesState['agencies'],
  agencyProviderPackage: AgencyProviderPackageModel,
  providerPackages: EntitiesState['providerPackages'],
  sortKey: string,
}
const handleSort = (params: HandleSortParams) => {
  const {
    agencies, agencyProviderPackage, providerPackages, sortKey,
  } = params

  if (sortKey === 'provider_package_id'){
    return providerPackages[agencyProviderPackage.provider_package_id]?.title
  }

  return agencies[agencyProviderPackage.agency_id]?.title
}

type AgencyProviderPackageFilters = {
  agency_id?: number,
  agency_title?: string,
  hidden?: 'true' | 'false',
  package_title?: string,
  provider_id?: number,
  provider_package_id?: number,
}

type UseAgencyProviderPackagesOptions = {
  entityKey?: string,
  filters?: AgencyProviderPackageFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgencyProviderPackageRequestOptions,
  sortKey?: string,
}

function useAgencyProviderPackages(options: UseAgencyProviderPackagesOptions) {
  const {
    entityKey, filters, requestOptions, sortKey = 'agency_id',
  } = options
  const {
    agency_id: filterAgencyId,
    agency_title: filterAgencyTitle,
    hidden: filterHidden,
    package_title: filterPackageTitle,
    provider_id: filterProviderId,
    provider_package_id: filterProviderPackageId,
  } = filters || {}

  const {
    updatedEntities: {
      agencyProviderPackages: agencyProviderPackagesUpdatedAt,
      agencies: agenciesUpdatedAt,
      providerPackages: providerPackagesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    agencies,
    agencyProviderPackages,
    providerPackages,
  } = useSelector(reduxState => reduxState.entities)

  const filteredAgencyProviderPackages = useMemo(() => {
    const filtered = Object.values(agencyProviderPackages).filter((agencyProviderPackage) => {
      const { agency_id, hidden, provider_package_id } = agencyProviderPackage

      const providerPackage = providerPackages[provider_package_id] || {}
      const { provider_id, title: providerTitle } = providerPackage

      const agency = agencies[agency_id] || {}
      const { title: agencyTitle } = agency

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)
      const matchAgencyTitle = matchFilterString(agencyTitle, filterAgencyTitle)
      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)
      const matchProviderPackageId = matchFilterNumber(provider_package_id, filterProviderPackageId)
      const matchProviderPackageTitle = matchFilterString(providerTitle, filterPackageTitle)

      return matchAgencyId
      && matchAgencyTitle
      && matchHidden
      && matchProviderId
      && matchProviderPackageId
      && matchProviderPackageTitle
    })

    return sortArrayBy(
      filtered,
      'asc',
      agencyProviderPackage => handleSort({
        agencies, agencyProviderPackage, providerPackages, sortKey,
      }),
    )
  }, [agenciesUpdatedAt, providerPackagesUpdatedAt, agencyProviderPackagesUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyProviderPackagesCount = filteredAgencyProviderPackages.length
  const hasFilteredAgencyProviderPackages = !!filteredAgencyProviderPackagesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyProviderPackagesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loaded, loading: loadingAgencyProviderPackages } = useReduxAction(
    'agencyProviderPackages',
    'loadAgencyProviderPackages',
    {
      entityKey,
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [agencyProviderPackagesUpdatedAt, filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyProviderPackages,
    filteredAgencyProviderPackagesCount,
    hasFilteredAgencyProviderPackages,
    loaded,
    loading: loadingAgencyProviderPackages,
  }
}

export default useAgencyProviderPackages
